(() => {
  const STORAGE_KEY = "hyped-email-popup";

  const readState = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  };

  const writeState = (next) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota / private mode */
    }
  };

  const pickWeighted = (prizes) => {
    const pool = prizes.filter((prize) => prize && typeof prize.label === "string");
    if (pool.length === 0) {
      return null;
    }
    const total = pool.reduce(
      (sum, prize) => sum + Math.max(1, Number(prize.weight) || 1),
      0,
    );
    let cursor = Math.random() * total;
    for (const prize of pool) {
      cursor -= Math.max(1, Number(prize.weight) || 1);
      if (cursor <= 0) {
        return prize;
      }
    }
    return pool[pool.length - 1] ?? null;
  };

  const segmentAngle = (index, count) => {
    const slice = 360 / Math.max(1, count);
    return index * slice + slice / 2;
  };

  const shouldOpen = (root) => {
    if (root.dataset.enabled !== "true" && root.dataset.designMode !== "true") {
      return false;
    }
    const frequency = root.dataset.frequency || "once";
    const state = readState();
    if (frequency === "always") {
      return true;
    }
    if (frequency === "session") {
      try {
        return window.sessionStorage.getItem(STORAGE_KEY) !== "1";
      } catch {
        return true;
      }
    }
    return state.completed !== true && state.dismissed !== true;
  };

  const markDismissed = (root) => {
    const frequency = root.dataset.frequency || "once";
    if (frequency === "session") {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
      return;
    }
    if (frequency === "once") {
      writeState({ ...readState(), dismissed: true });
    }
  };

  const markCompleted = (root) => {
    const frequency = root.dataset.frequency || "once";
    if (frequency === "session") {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* ignore */
      }
      return;
    }
    if (frequency === "once") {
      writeState({ ...readState(), completed: true });
    }
  };

  const parsePrizes = (root) => {
    const script = root.querySelector("[data-email-popup-prizes]");
    if (!(script instanceof HTMLScriptElement)) {
      return [];
    }
    try {
      const parsed = JSON.parse(script.textContent ?? "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const showPanel = (root, name) => {
    root.querySelectorAll("[data-popup-panel]").forEach((panel) => {
      if (!(panel instanceof HTMLElement)) {
        return;
      }
      panel.hidden = panel.dataset.popupPanel !== name;
    });
  };

  const setPrizeUi = (root, prize) => {
    const label = root.querySelector("[data-popup-prize-label]");
    const code = root.querySelector("[data-popup-prize-code]");
    const claim = root.querySelector("[data-popup-claim]");
    const lose = Boolean(prize?.is_lose) || !prize?.discount_code;
    if (label instanceof HTMLElement) {
      label.textContent = prize?.label || "Thanks for joining";
    }
    if (code instanceof HTMLElement) {
      code.hidden = lose;
      code.textContent = prize?.discount_code || "";
    }
    if (claim instanceof HTMLAnchorElement) {
      claim.hidden = lose;
      if (!lose && prize?.discount_code) {
        claim.href = `/discount/${encodeURIComponent(prize.discount_code)}`;
      }
    }
  };

  const escapeXml = (value) =>
    String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");

  const readAccent = (root) => {
    const raw = getComputedStyle(root).getPropertyValue("--popup-accent").trim();
    return raw || "#a855f7";
  };

  const renderWheel = (root, prizes) => {
    const svg = root.querySelector("[data-popup-wheel-svg]");
    if (!(svg instanceof SVGElement)) {
      return;
    }
    const count = Math.max(1, prizes.length);
    const cx = 100;
    const cy = 100;
    const radius = 98;
    let markup = "";

    for (let index = 0; index < count; index += 1) {
      const start = (index / count) * Math.PI * 2 - Math.PI / 2;
      const end = ((index + 1) / count) * Math.PI * 2 - Math.PI / 2;
      const x1 = cx + radius * Math.cos(start);
      const y1 = cy + radius * Math.sin(start);
      const x2 = cx + radius * Math.cos(end);
      const y2 = cy + radius * Math.sin(end);
      const large = end - start > Math.PI ? 1 : 0;
      const mid = (start + end) / 2;
      // Sit labels toward the outer rim, reading long-ways across the slice
      // (tangential) instead of stacked toward the center (radial).
      const labelR = 72;
      const lx = cx + labelR * Math.cos(mid);
      const ly = cy + labelR * Math.sin(mid);
      let rot = (mid * 180) / Math.PI + 90;
      // Keep text upright: flip when it would read upside-down.
      if (rot > 90 && rot < 270) {
        rot -= 180;
      }
      const rawLabel = String(prizes[index]?.label || `Prize ${index + 1}`);
      const label = escapeXml(
        rawLabel.length > 16 ? `${rawLabel.slice(0, 15)}…` : rawLabel,
      );
      const toneClass =
        index % 2 === 0
          ? "hyped-email-popup__seg hyped-email-popup__seg--a"
          : "hyped-email-popup__seg hyped-email-popup__seg--b";

      markup += `<path class="${toneClass}" d="M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z"></path>`;
      markup += `<text class="hyped-email-popup__seg-label" x="${lx}" y="${ly}" transform="rotate(${rot} ${lx} ${ly})">${label}</text>`;
    }

    markup += `<circle class="hyped-email-popup__seg-ring" cx="${cx}" cy="${cy}" r="${radius - 1.5}"></circle>`;
    svg.innerHTML = markup;
    root.style.setProperty("--popup-accent-resolved", readAccent(root));
  };

  const submitCustomerForm = async (form, prize) => {
    const tagsInput = form.querySelector("[data-popup-tags]");
    if (tagsInput instanceof HTMLInputElement) {
      const base = tagsInput.dataset.baseTags || "email-popup,newsletter";
      const prizeTag = prize?.discount_code
        ? `popup-prize-${prize.discount_code}`
        : prize?.label
          ? `popup-prize-${prize.label}`
          : "";
      tagsInput.value = [base, prizeTag]
        .filter((part) => part && part.trim().length > 0)
        .join(",");
    }

    writeState({
      ...readState(),
      lastPrize: prize
        ? {
            label: prize.label || "",
            discount_code: prize.discount_code || "",
            is_lose: Boolean(prize.is_lose),
          }
        : null,
    });

    const body = new FormData(form);
    const action = form.getAttribute("action") || "/contact";
    const response = await fetch(action, {
      method: "POST",
      body,
      headers: {
        Accept: "text/html,application/xhtml+xml",
      },
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error("Shopify customer signup failed");
    }
    return true;
  };

  const goToEmailCapture = (root, prize, emailInput) => {
    const pending = root.querySelector("[data-popup-pending-label]");
    const captureSub = root.querySelector("[data-popup-capture-sub]");
    if (pending instanceof HTMLElement) {
      pending.hidden = false;
      pending.textContent = prize?.is_lose
        ? prize.label || "Nice try"
        : `You got: ${prize?.label || "a reward"}`;
    }
    if (captureSub instanceof HTMLElement) {
      captureSub.hidden = false;
      captureSub.textContent = prize?.is_lose
        ? "Enter your email anyway for future drops."
        : "Enter your email to unlock the code in Shopify.";
    }
    showPanel(root, "capture");
    window.setTimeout(() => {
      if (emailInput instanceof HTMLInputElement) {
        emailInput.focus();
      }
    }, 40);
  };

  const initializeEmailPopup = (scope = document) => {
    const roots = scope.querySelectorAll("[data-email-popup]");
    roots.forEach((root) => {
      if (!(root instanceof HTMLElement) || root.dataset.bound === "true") {
        return;
      }
      root.dataset.bound = "true";

      const form = root.querySelector("form");
      const wheel = root.querySelector("[data-popup-wheel]");
      const emailInput = root.querySelector("[data-popup-email]");
      const errorEl = root.querySelector("[data-popup-error]");
      const spinButton = root.querySelector("[data-popup-spin]");
      const style = root.dataset.style || "simple";
      const prizes = parsePrizes(root);
      let busy = false;
      let pendingPrize = null;
      let wheelRotation = 0;

      if (style === "spin") {
        renderWheel(root, prizes);
      }

      const initialPanel = style === "simple" ? "capture" : "play";

      const open = () => {
        root.hidden = false;
        root.setAttribute("data-open", "true");
        document.documentElement.classList.add("hyped-popup-open");
        showPanel(root, initialPanel);
        if (initialPanel === "capture") {
          window.setTimeout(() => {
            if (emailInput instanceof HTMLInputElement) {
              emailInput.focus();
            }
          }, 40);
        }
      };

      const close = (dismiss = false) => {
        root.setAttribute("data-open", "false");
        root.hidden = true;
        document.documentElement.classList.remove("hyped-popup-open");
        if (dismiss) {
          markDismissed(root);
        }
      };

      root.querySelectorAll("[data-popup-close]").forEach((button) => {
        button.addEventListener("click", () => close(true));
      });

      root.addEventListener("click", (event) => {
        if (event.target === root) {
          close(true);
        }
      });

      window.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && root.getAttribute("data-open") === "true") {
          close(true);
        }
      });

      const finishSuccess = (prize) => {
        setPrizeUi(root, prize);
        showPanel(root, "success");
        markCompleted(root);
      };

      const runWheelSpin = async () => {
        if (!(wheel instanceof HTMLElement)) {
          return null;
        }
        const prize = pickWeighted(prizes);
        if (!prize) {
          throw new Error("Add at least one prize block");
        }
        const index = Math.max(
          0,
          prizes.findIndex((item) => item === prize),
        );
        const turns = 5 + Math.floor(Math.random() * 3);
        const landAt = (360 - segmentAngle(index, prizes.length) + 360) % 360;
        const current = ((wheelRotation % 360) + 360) % 360;
        let delta = (landAt - current + 360) % 360;
        if (delta < 20) {
          delta += 360;
        }
        wheelRotation += turns * 360 + delta;
        const cta = root.querySelector("[data-popup-wheel-cta]");
        if (cta instanceof HTMLElement) {
          cta.hidden = true;
        }
        wheel.style.transition = "transform 4.2s cubic-bezier(0.12, 0.75, 0.12, 1)";
        wheel.style.transform = `rotate(${wheelRotation}deg)`;
        await new Promise((resolve) => window.setTimeout(resolve, 4300));
        return prize;
      };

      if (spinButton instanceof HTMLElement) {
        spinButton.addEventListener("click", async () => {
          if (busy || style !== "spin") {
            return;
          }
          busy = true;
          root.setAttribute("data-busy", "true");
          spinButton.setAttribute("disabled", "true");
          try {
            pendingPrize = await runWheelSpin();
            goToEmailCapture(root, pendingPrize, emailInput);
          } catch (error) {
            console.error(error);
            spinButton.removeAttribute("disabled");
            const cta = root.querySelector("[data-popup-wheel-cta]");
            if (cta instanceof HTMLElement) {
              cta.hidden = false;
            }
          } finally {
            busy = false;
            root.removeAttribute("data-busy");
          }
        });
      }

      root.querySelectorAll("[data-popup-box]").forEach((box) => {
        box.addEventListener("click", async () => {
          if (busy || style !== "mystery" || !(box instanceof HTMLElement)) {
            return;
          }
          if (box.classList.contains("is-opening") || box.classList.contains("is-picked")) {
            return;
          }
          busy = true;
          root.setAttribute("data-busy", "true");
          pendingPrize = pickWeighted(prizes);
          root.querySelectorAll("[data-popup-box]").forEach((other) => {
            if (other instanceof HTMLElement && other !== box) {
              other.classList.add("is-dimmed");
              other.setAttribute("disabled", "true");
            }
          });
          box.classList.add("is-opening", "is-picked");
          await new Promise((resolve) => window.setTimeout(resolve, 850));
          goToEmailCapture(root, pendingPrize, emailInput);
          busy = false;
          root.removeAttribute("data-busy");
        });
      });

      if (form instanceof HTMLFormElement) {
        form.addEventListener("submit", async (event) => {
          event.preventDefault();
          if (busy) {
            return;
          }
          if (!(emailInput instanceof HTMLInputElement)) {
            return;
          }
          if (errorEl instanceof HTMLElement) {
            errorEl.hidden = true;
            errorEl.textContent = "";
          }
          if (!emailInput.checkValidity()) {
            emailInput.reportValidity();
            return;
          }

          const prize =
            pendingPrize ||
            prizes.find((item) => !item.is_lose && item.discount_code) ||
            prizes[0] ||
            null;

          if (style !== "simple" && !pendingPrize) {
            if (errorEl instanceof HTMLElement) {
              errorEl.hidden = false;
              errorEl.textContent =
                style === "spin"
                  ? "Spin the wheel first, then enter your email."
                  : "Pick a mystery box first, then enter your email.";
            }
            showPanel(root, "play");
            return;
          }

          busy = true;
          root.setAttribute("data-busy", "true");
          try {
            await submitCustomerForm(form, prize);
            finishSuccess(prize);
          } catch (error) {
            if (errorEl instanceof HTMLElement) {
              errorEl.hidden = false;
              errorEl.textContent =
                "Could not save your email. Check the address and try again.";
            }
            console.error(error);
          } finally {
            busy = false;
            root.removeAttribute("data-busy");
          }
        });
      }

      root.querySelector("[data-popup-copy]")?.addEventListener("click", async () => {
        const code = root.querySelector("[data-popup-prize-code]");
        if (!(code instanceof HTMLElement) || !code.textContent) {
          return;
        }
        try {
          await navigator.clipboard.writeText(code.textContent);
          const button = root.querySelector("[data-popup-copy]");
          if (button instanceof HTMLElement) {
            const prev = button.textContent;
            button.textContent = "Copied";
            window.setTimeout(() => {
              button.textContent = prev;
            }, 1600);
          }
        } catch {
          /* ignore */
        }
      });

      const params = new URLSearchParams(window.location.search);
      const posted =
        root.dataset.posted === "true" ||
        params.get("customer_posted") === "true" ||
        params.get("contact_posted") === "true";

      if (posted) {
        const saved = readState().lastPrize;
        open();
        finishSuccess(
          saved || prizes.find((item) => item.discount_code) || prizes[0],
        );
        return;
      }

      if (root.dataset.designMode === "true") {
        open();
        return;
      }

      if (!shouldOpen(root)) {
        root.hidden = true;
        return;
      }

      const delayMs = Math.max(0, Number(root.dataset.delaySeconds || 3) * 1000);
      window.setTimeout(() => {
        if (shouldOpen(root)) {
          open();
        }
      }, delayMs);
    });
  };

  initializeEmailPopup();
  document.addEventListener("shopify:section:load", (event) => {
    initializeEmailPopup(event.target instanceof Element ? event.target : document);
  });
})();
